import { apiGet, apiPost, ApiError } from './client';
import type { AuthUser } from '../types';

const TOKEN_KEY = 'token';

interface AuthResponse {
  token: string;
  user: AuthUser;
}

// REAL backend calls to the Express auth routes.
export async function loginRequest(email: string, password: string): Promise<AuthUser> {
  const res = await apiPost<AuthResponse>('/auth/login', { email, password });
  localStorage.setItem(TOKEN_KEY, res.token);
  return res.user;
}

export async function registerRequest(
  name: string,
  email: string,
  password: string,
): Promise<AuthUser> {
  const res = await apiPost<AuthResponse>('/auth/signup', { name, email, password });
  localStorage.setItem(TOKEN_KEY, res.token);
  return res.user;
}

// Exchanges a verified Firebase ID token for our own session (find-or-create user).
export async function googleSignInRequest(idToken: string): Promise<AuthUser> {
  const res = await apiPost<AuthResponse>('/auth/google', { idToken });
  localStorage.setItem(TOKEN_KEY, res.token);
  return res.user;
}

// Exchanges a verified Firebase phone OTP ID token for our own session (find-or-create user).
export async function phoneSignInRequest(idToken: string, name?: string): Promise<AuthUser> {
  const res = await apiPost<AuthResponse>('/auth/phone', { idToken, name });
  localStorage.setItem(TOKEN_KEY, res.token);
  return res.user;
}

// Verifies the current session against the backend (GET /api/auth/me).
// Works with Bearer token or the shared HttpOnly SSO cookie.
// If a stale Bearer fails, drop it and retry once with cookie-only SSO.
export async function fetchCurrentUser(): Promise<AuthUser> {
  try {
    return await apiGet<AuthUser>('/auth/me');
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0;
    if ((status === 401 || status === 403) && localStorage.getItem(TOKEN_KEY)) {
      localStorage.removeItem(TOKEN_KEY);
      return apiGet<AuthUser>('/auth/me');
    }
    throw err;
  }
}

export function forgotPassword(email: string): Promise<{ message: string; devResetToken?: string }> {
  return apiPost('/auth/forgot-password', { email });
}

export function resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
  return apiPost('/auth/reset-password', { token, newPassword });
}

export async function logoutRequest(): Promise<void> {
  try {
    await apiPost('/auth/logout', {});
  } catch {
    // Cookie clear is best-effort; local session is always dropped.
  }
}

export function hasToken(): boolean {
  return Boolean(localStorage.getItem(TOKEN_KEY));
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  // Remove any legacy/dummy auth values that older builds may have persisted.
  localStorage.removeItem('user');
}
